import React, {
  useState,
  PropsWithChildren,
  ReactNode,
} from 'react';
import {
  useFormContext,
  useWatch,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import {
  CcmPCCConsentDTO,
  PCCConsentFlagDTO,
} from 'dtos';
import ConsentField from 'pages/Dashboard/pages/Charts/pages/Details/components/PCC/ConsentField';
import ConsentToggle from 'pages/Dashboard/pages/Charts/pages/Details/components/PCC/ConsentToggle';
import type {
  PCCForm,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/PCC/types';
import startCase from 'lodash/startCase';
import {
  formatDate,
} from 'utils/date';
import isNil from 'lodash/isNil';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import Accordion from 'core/components/Accordion';
import Skeleton from 'core/components/Skeleton';
import {
  notEmptyString,
} from '@populateui/utils';
import Button from 'pages/Dashboard/components/Button';
import Tooltip from 'core/components/Tooltip';
import clsx from 'clsx';
import {
  formatConsentUpdatedByName,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/PCC/helper';
import SendConsentFormDialog from 'pages/Dashboard/pages/Charts/pages/Details/components/PCC/SendConsentFormDialog';

type Props = {
  header: string;
  consent?: CcmPCCConsentDTO;
  consentFlag?: PCCConsentFlagDTO;
  consentGivenByName: string | null;
  showEligibility: boolean;
  isCcmApplicable: boolean;
  isLoading?: boolean;
  patientId?: number;
  patientEmail?: string | null;
  patientPhone?: string | null;
};

function Row({
  children,
}: PropsWithChildren) {
  return (
    <div className="p-2 flex flex-col gap-2">
      {children}
    </div>
  );
}

type FieldGridProps = PropsWithChildren<{
  columnsClassName: string;
}>;

function FieldGrid({
  children,
  columnsClassName,
}: FieldGridProps) {
  return (
    <div className={columnsClassName}>
      {children}
    </div>
  );
}

type ConsentGroupProps = {
  label: string;
  children: ReactNode;
};

function ConsentGroup({
  label,
  children,
}: ConsentGroupProps) {
  return (
    <Accordion
      defaultExpanded
      header={label}
      disableGutters
      headerClasses={{
        root: '!px-3 !py-1 !min-h-[initial] !bg-gray-100 !text-gray-600 !font-semibold text-sm',
        content: '!m-0',
      }}
      detailsClasses={{ root: '!p-0 flex flex-col gap-2' }}
    >
      {children ?? ''}
    </Accordion>
  );
}

function formatConsentDate(value?: string | null): string {
  return isNil(value) ? DEFAULT_EMPTY_VALUE : formatDate(value ?? '');
}

type RowSkeletonProps = {
  fieldCount: number;
};

function RowSkeleton({
  fieldCount,
}: RowSkeletonProps) {
  return (
    <Row>
      <Skeleton className="!h-10 !w-56" />
      <FieldGrid columnsClassName="grid grid-cols-1 gap-3 lg:grid-cols-4">
        {Array.from({ length: fieldCount }, (_, index) => (
          <div key={index} className="flex flex-col gap-1">
            <Skeleton className="!h-3 !w-24" variant="text" />
            <Skeleton className="!h-5 !w-full" variant="text" />
          </div>
        ))}
      </FieldGrid>
    </Row>
  );
}

const toggleClasses = 'flex items-center gap-3';

export default function ConsentAccordionSection({
  header,
  consent,
  consentFlag,
  consentGivenByName,
  showEligibility,
  isCcmApplicable,
  isLoading = false,
  patientId = 0,
  patientEmail,
  patientPhone,
}: Props) {
  const { control } = useFormContext<PCCForm>();
  const hasConsentedToCommunication = useWatch({ name: 'hasConsentedToCommunication', control });
  const [showSendConsentDialog, setShowSendConsentDialog] = useState(false);
  return (
    <>
      <ConsentGroup label="Communications">
        <Row>
          <div className={toggleClasses}>
            <Controller
              name="hasConsentedToCommunication"
              control={control}
              render={({ field }) => (
                <ConsentToggle
                  checked={field.value}
                  label="Consented to Communication"
                  onChange={field.onChange}
                />
              )}
            />
          </div>
          <FieldGrid columnsClassName="grid grid-cols-1 gap-3 lg:grid-cols-4">
            <ConsentField
              id={`${header}-consent-given-on`}
              label="Collected On"
              value={formatConsentDate(consentFlag?.updatedOn)}
            />
            <ConsentField
              id={`${header}-consent-given-by`}
              label="Collected By"
              value={consentGivenByName}
            />
            <ConsentField
              id={`${header}-consent-source`}
              label="Source"
              value={startCase(consentFlag?.source ?? '')}
            />
          </FieldGrid>

          <div>
            {/* button to Send consent form */}
            <Tooltip title="Consent has already been collected" hide={!hasConsentedToCommunication}>
              <span className={clsx({ 'cursor-not-allowed !w-fit !block': hasConsentedToCommunication })}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={hasConsentedToCommunication ?? false}
                  className="!w-fit"
                  onClick={() => { setShowSendConsentDialog(true); }}
                >
                  Send Consent Form
                </Button>
              </span>
            </Tooltip>
          </div>
        </Row>
      </ConsentGroup>
      {isCcmApplicable && (isLoading ? (
        <ConsentGroup label={header}>
          {showEligibility && <RowSkeleton fieldCount={1} />}
          <RowSkeleton fieldCount={2} />
        </ConsentGroup>
      ) : (
        <ConsentGroup label={header}>
          {showEligibility && (
            <>
              <Row>
                <div className={toggleClasses}>
                  <Controller
                    name="ccm.isEligible"
                    control={control}
                    render={({ field }) => (
                      <ConsentToggle
                        checked={field.value}
                        label={`Eligible for ${header.split(' ')[0]}`}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </div>
                <FieldGrid columnsClassName="grid grid-cols-1 gap-3 lg:grid-cols-4">
                  <ConsentField
                    id={`${header}-eligibility-source`}
                    label="Eligibility Source"
                    value={startCase(consent?.eligibility?.source ?? '')}
                  />
                </FieldGrid>
              </Row>
              {/* <Divider className="-mx-1" /> */}
            </>
          )}
          <Row>
            <div className={toggleClasses}>
              <Controller
                name="ccm.isOptedOut"
                control={control}
                render={({ field }) => (
                  <ConsentToggle
                    checked={field.value}
                    label="Opted Out"
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
            <FieldGrid columnsClassName="grid grid-cols-1 gap-3 lg:grid-cols-4">
              <ConsentField
                id={`${header}-opted-out-on`}
                label="Opted Out On"
                value={formatConsentDate(consent?.optOut?.updatedOn)}
              />
              <ConsentField
                id={`${header}-opt-out-by`}
                label="Collected By"
                value={formatConsentUpdatedByName(consent?.optOut?.updatedBy)}
              />
              <ConsentField
                id={`${header}-opt-out-source`}
                label="Opt Out Source"
                value={notEmptyString(consent?.optOut?.source) ? startCase(consent?.optOut?.source ?? '') : DEFAULT_EMPTY_VALUE}
              />
            </FieldGrid>
          </Row>
        </ConsentGroup>
      ))}
      {showSendConsentDialog ? (
        <SendConsentFormDialog
          patientId={patientId}
          patientEmail={patientEmail}
          patientPhone={patientPhone}
          onClose={() => { setShowSendConsentDialog(false); }}
        />
      ) : null}
    </>
  );
}
